import { h, defineComponent } from 'vue';
import { level, pageTitle } from '@/ui/components';
import {
  hasPermission,
  Permissions,
  isOrganizationAdministrator,
  hasPublicApiEnabled,
} from '@/ui/auth/auth';
import { t } from '@/ui/i18n/i18n';
import * as qs from '@/ui/queryState';
import { secondaryNavbar, TabItem } from '@/ui/components/navigation/SecondaryNavbar';
import { AdministrationTabNames, queryTabName } from '@/ui/components/navigation/constants';
import { UserIndexPage } from '@/ui/pages/user/UserIndexPage';
import { GroupIndexPage } from '@/ui/pages/group/GroupIndexPage';
import { RoleIndexPage } from '@/ui/pages/role/RoleIndexPage';
import { CategoryIndexPage } from '@/ui/pages/category/CategoryIndexPage';
import PublicApiIndex from '@/ui/pages/publicApi/PublicApiIndex.vue';
import { AddUserButton } from '@/ui/components/button/AddUserButton';
import { PublicApiButtons } from '@/ui/pages/publicApi/PublicApiButtons';
import { getKeyByValueFromEnumeration } from '@/ui/helpers';
import { RedirectButton } from '@/ui/components/button/RedirectButton';
import SsoIndex from '@/ui/pages/sso/SsoIndex.vue';
import { ssoIcon } from '@/ui/components/icon/icons';

export const AdministrationPage = defineComponent({
  name: 'AdministrationPage',
  setup() {
    const startTab = (): AdministrationTabNames => {
      switch (true) {
        case hasPermission(Permissions.MANAGE_USERS):
          return AdministrationTabNames.USER;
        case hasPermission(Permissions.MANAGE_PERMISSIONS):
          return AdministrationTabNames.ROLE;
        case hasPermission(Permissions.MANAGE_CATEGORIES):
          return AdministrationTabNames.CATEGORY;
        default:
          return AdministrationTabNames.USER;
      }
    };

    const activeTab = qs.stringEnum<AdministrationTabNames>(
      queryTabName,
      Object.values(AdministrationTabNames),
      startTab(),
      false,
    );

    const secondaryTabItems : TabItem[] = [
      {
        value: AdministrationTabNames.USER,
        label: t('common.users'),
        icon: 'user',
        tooltipLabel: t('feature-explanation.users'),
        underLineColor: 'blue',
        hide: !hasPermission(Permissions.MANAGE_USERS),
      },
      {
        value: AdministrationTabNames.GROUP,
        label: t('common.groups'),
        icon: 'user-friends',
        tooltipLabel: t('feature-explanation.groups'),
        underLineColor: 'green',
        hide: !hasPermission(Permissions.MANAGE_USERS),
      },
      {
        value: AdministrationTabNames.ROLE,
        label: t('common.roles'),
        icon: 'user-tie',
        tooltipLabel: t('feature-explanation.roles'),
        underLineColor: 'purple',
        starred: true,
        hide: !hasPermission(Permissions.MANAGE_PERMISSIONS),
      },
      {
        value: AdministrationTabNames.CATEGORY,
        label: t('common.categories'),
        icon: 'tag',
        tooltipLabel: t('feature-explanation.categories'),
        underLineColor: 'blue',
        starred: true,
        hide: !hasPermission(Permissions.MANAGE_CATEGORIES),
      },
      {
        value: AdministrationTabNames.API_KEY,
        label: t('common.api-keys'),
        icon: 'key',
        tooltipLabel: t('api-keys.tooltips.title'),
        underLineColor: 'green',
        hide: !isOrganizationAdministrator(),
      },
      {
        value: AdministrationTabNames.SSO,
        icon: ssoIcon(),
        label: t('common.sso'),
        tooltipLabel: t('sso.tooltips.title'),
        underLineColor: 'green',
        hide: !isOrganizationAdministrator(),
      },
    ];

    const getTab = () => {
      switch (activeTab.value) {
        case AdministrationTabNames.USER:
          return UserIndexPage;
        case AdministrationTabNames.GROUP:
          return GroupIndexPage;
        case AdministrationTabNames.ROLE:
          return RoleIndexPage;
        case AdministrationTabNames.CATEGORY:
          return CategoryIndexPage;
        case AdministrationTabNames.API_KEY:
          return PublicApiIndex;
        case AdministrationTabNames.SSO:
          return SsoIndex;
        default:
          throw new Error('Invalid tab selected. Please use AdministrationTabEnum enumeration');
      }
    };

    const renderButton = () => {
      switch (activeTab.value) {
        case AdministrationTabNames.USER:
          return h(AddUserButton);
        case AdministrationTabNames.GROUP:
        case AdministrationTabNames.ROLE:
        case AdministrationTabNames.CATEGORY:
          return h(RedirectButton, {
            props: {
              routeName: `${getKeyByValueFromEnumeration(activeTab.value, AdministrationTabNames).toLowerCase()}Create`,
              text: t(`${getKeyByValueFromEnumeration(activeTab.value, AdministrationTabNames).toLowerCase()}.add`),
              icon: 'plus',
            },
          });
        case AdministrationTabNames.API_KEY:
          return hasPublicApiEnabled() ? h(PublicApiButtons) : null;
        default:
          return null;
      }
    };

    return function render() {
      return h('div', [
        level(
          [
            pageTitle(t('navbar.administration')),
          ],
          [
            renderButton(),
          ],
        ),
        secondaryNavbar({
          props: { value: activeTab.value, tabItems: secondaryTabItems },
          on: {
            input: (value: AdministrationTabNames) => {
              activeTab.value = value;
            },
          },
        }),
        h(getTab()),
      ]);
    };
  },
});
